/**
 * Thin fetch wrapper.
 *
 * Authentication is an HttpOnly session cookie (ARCHITECTURE.md §6.1), so there
 * is no token to store or attach here — the browser sends it automatically. The
 * only thing this layer adds is the CSRF header for state-changing requests,
 * read from the non-HttpOnly companion cookie.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

const CSRF_COOKIE = "summit_csrf";
const CSRF_HEADER = "X-CSRF-Token";

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  const response = await fetch(path, {
    method,
    headers,
    // Same-origin in both dev (Vite proxy) and production (IIS), so this is all
    // the cookie handling needed.
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const detail =
      (payload && typeof payload.detail === "string" && payload.detail) ||
      `Request failed (${response.status})`;
    throw new ApiError(response.status, detail);
  }

  return payload as T;
}

/**
 * GET a file and hand it to the browser to save.
 *
 * Fetched rather than linked so a refusal arrives as an ApiError the page can
 * show, instead of navigating away to a page of JSON. The name comes from the
 * server's Content-Disposition, which knows the project and date.
 */
async function download(path: string): Promise<void> {
  const response = await fetch(path, { credentials: "same-origin" });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const detail =
      (payload && typeof payload.detail === "string" && payload.detail) ||
      `Download failed (${response.status})`;
    throw new ApiError(response.status, detail);
  }

  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? "download";

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  download,
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

// --- Shapes returned by the API ------------------------------------------- //

export interface User {
  id: number;
  email: string;
  display_name: string | null;
  system_role: "admin" | "viewer";
  status: "invited" | "active" | "disabled";
}

export interface Me {
  user: User;
  projects: string[];
}

export interface ProfileUpdate {
  /** null clears the name; the server trims and collapses whitespace. */
  display_name: string | null;
}

export interface ProjectAccessUser {
  id: number;
  email: string;
  display_name: string | null;
  system_role: "admin" | "viewer";
  /** True for an explicit grant, or trivially for an admin (§6.3). */
  has_access: boolean;
}

export interface UserInviteRequest {
  email: string;
  display_name?: string | null;
  system_role?: "admin" | "viewer";
  /** Ignored for admins, who already see every project. */
  project_slugs?: string[];
}

/**
 * Must match `ProfileUpdate.display_name` on the server, which rejects longer
 * values with a 422. Declared once so the account form and the admin grid
 * cannot drift apart.
 */
export const NAME_MAX_LENGTH = 50;

export interface ProjectSummary {
  slug: string;
  title: string;
  /** Shown on the project's tile in place of `title` when set. */
  short_title: string | null;
  description: string | null;
  /** A Mantine color name (e.g. "green"), or null for the default surface. */
  color: string | null;
  has_error: boolean;
  config_error: string | null;
}

export interface DatasetSummary {
  name: string;
  columns: string[];
  types: Record<string, string>;
}

// --- Layout (§7.1) --------------------------------------------------------- //
//
// Widgets are addressed by id. `group_by` and `agg` are here for labelling
// only — the server reads the actual query from dashboard.yaml, so the client
// cannot ask for something the config did not declare.

export interface FooterItem {
  label: string;
  /** True pairs this label with AggregateResult.footer_values at the same
   * index; false means the label is the whole line (a static note). */
  has_value: boolean;
}

export interface StatTileWidget {
  id: string;
  title: string;
  agg: string;
  precision: number | null;
  /** A Mantine color name (e.g. "blue"), or null for the default ink color. */
  color: string | null;
  /** A key into STAT_TILE_ICONS (viz/statTileIcons.tsx), or null for none. */
  icon: string | null;
  /** Smaller lines at the tile's foot, in order. */
  footer: FooterItem[];
}

export interface ColorThreshold {
  /** A bar with a value greater than this gets `color`. */
  min: number;
  /** A Mantine color name (e.g. "red"). Ignored by `type: "pie"`. */
  color: string;
}

export interface ChartWidget {
  id: string;
  type: "bar" | "pie";
  title: string;
  group_by: string;
  agg: string;
  thresholds: ColorThreshold[];
}

export interface RowColors {
  /** May be a column the table does not display. */
  column: string;
  /** Declared order is legend order. */
  rules: { value: string; color: string }[];
}

export interface TableWidget {
  type: "table";
  id: string;
  title: string;
  columns: string[];
  /** Display text by column name; a column absent here shows its own name. */
  labels: Record<string, string>;
  row_colors: RowColors | null;
  page_size: number;
  filters: string[];
  /** Non-empty means show a search box matching these columns. */
  search: string[];
  /** Offer the selected rows as an XLSX file. */
  download: boolean;
}

export interface TextTileWidget {
  type: "text_stat";
  id: string;
  title: string;
}

export interface StatusStyle {
  value: string;
  label: string;
  color: string;
}

export interface MapBlock {
  type: "map";
  id: string;
  title: string;
  height: number;
  /** Label each cluster with its key; overlapping labels are hidden. */
  labels: boolean;
  /** Declared order is legend order. */
  statuses: StatusStyle[];
}

export type LayoutBlock =
  | { type: "stat_row"; tiles: StatTileWidget[] }
  | { type: "row"; widgets: ChartWidget[] }
  | TableWidget
  | { type: "markdown"; source: string }
  | TextTileWidget
  | MapBlock;

export interface ProjectDetail {
  slug: string;
  title: string;
  description: string | null;
  partner_name: string | null;
  partner_url: string | null;
  /** A Mantine color name (e.g. "green"), or null for the default surface. */
  color: string | null;
  datasets: DatasetSummary[];
  layout: LayoutBlock[];
  config_error: string | null;
}

export interface AggregateResult {
  dataset: string;
  group_by: string | null;
  agg: string;
  column: string | null;

  widget_id: string | null;
  /**
   * "scalar" is a stat tile: `value` is set and `keys`/`values` are empty.
   * "text" is a text tile: `text` is set instead, verbatim, unformatted.
   */
  kind: "series" | "scalar" | "text";
  value: number | null;
  precision: number | null;
  /** Set only when kind === "text". */
  text: string | null;
  /** One entry per tile.footer item, parallel to StatTileWidget.footer — null
   * at a note item's index. Set only when kind === "scalar". */
  footer_values: (number | null)[];

  keys: string[];
  values: (number | null)[];
  /** Set only for a map: one status per entry in `keys`, aligned to it. */
  statuses?: (string | null)[] | null;
  total_groups: number;
  total_rows: number;
  truncated: boolean;
  version: string;
}

export interface ProjectVersion {
  slug: string;
  /** Moves when any file in the project folder changes. */
  version: string;
}

/** One file in a project's download/ folder. */
export interface ReportFile {
  /** Filename without its extension: the label supervisors see. */
  name: string;
  filename: string;
  size_bytes: number;
  /** ISO 8601, UTC. */
  modified: string;
  /** HTML opens in the browser; every type can be downloaded. */
  viewable: boolean;
}

/** The Reports tab's contents. */
export interface ReportList {
  reports: ReportFile[];
  /** Files refused for their name (e.g. a "+"), to be renamed upstream. */
  illegal: string[];
}

/**
 * Where a report file is served. A plain URL rather than a fetch: View opens
 * it as a top-level page (sandboxed by the API), and Download lets the
 * browser's own download manager save it, which is what Android needs to put
 * the file in Downloads for offline use.
 */
export function reportUrl(slug: string, filename: string, download = false): string {
  return (
    `/api/reports/${encodeURIComponent(slug)}/${encodeURIComponent(filename)}` +
    (download ? "?download=1" : "")
  );
}

export interface TablePage {
  widget_id: string;
  columns: string[];
  /** Positional, parallel to `columns`. */
  rows: (string | number | boolean | null)[][];
  page: number;
  page_size: number;
  total_rows: number;
  /** Distinct values per filterable column, from the unfiltered data. */
  filter_options: Record<string, string[]>;
  /** Parallel to `rows`: a Mantine color to tint the row, or null. */
  row_colors: (string | null)[];
  version: string;
}

export interface SessionInfo {
  id: number;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  ip: string | null;
  user_agent: string | null;
  current: boolean;
}
